import { computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import {
    TourData,
    FolderData,
    DeliveryNoteData,
} from '@app/shared/models/store-types';
import { ViewType, UserRole } from '@app/shared/models/view-config.interface';
import { EventWithOrgSiteKey } from '@app/stores/eventsStore';
import { BundleGroupData } from '@app/stores/bundleGroupsStore';

// Union type for all possible selection items
export type SelectableItem =
    | TourData
    | FolderData
    | DeliveryNoteData
    | EventWithOrgSiteKey
    | BundleGroupData;

// Context data interface for better type safety
export interface SelectionContextData {
    userRole?: UserRole;
    viewType?: ViewType;
    organizationSiteKey?: string;
    [key: string]: any;
}

export interface SelectionState {
    selectedItems: SelectableItem[];
    selectionType: 'single' | 'multiple';
    lastSelectedItem: SelectableItem | null;
    contextData?: SelectionContextData;
}

export const initialSelectionState: SelectionState = {
    selectedItems: [],
    selectionType: 'multiple',
    lastSelectedItem: null,
    contextData: undefined,
};

// =============================================================================
// Pure type guard / filter functions — module-level exports (NOT on store)
// =============================================================================

export function hasStatus(
    item: SelectableItem
): item is SelectableItem & { status: string } {
    return 'status' in item && typeof item.status === 'string';
}

export function hasReadOnly(
    item: SelectableItem
): item is SelectableItem & { read_only: boolean } {
    return 'read_only' in item && typeof item.read_only === 'boolean';
}

export function hasKey(
    item: SelectableItem
): item is SelectableItem & { _key: string } {
    return '_key' in item && typeof item._key === 'string';
}

export function hasEventKey(
    item: SelectableItem
): item is SelectableItem & { eventKey: string } {
    return 'eventKey' in item && typeof item.eventKey === 'string';
}

export function getItemKey(item: SelectableItem): string {
    if (hasKey(item)) {
        return item._key;
    } else if (hasEventKey(item)) {
        return item.eventKey;
    }
    return 'unknown';
}

export function isFolderData(item: SelectableItem): item is FolderData {
    return 'bundleNumber' in item && 'consigneeKey' in item;
}

export function isTourData(item: SelectableItem): item is TourData {
    return 'tourNumber' in item && 'deliveryNoteKeys' in item;
}

export function isDeliveryNoteData(
    item: SelectableItem
): item is DeliveryNoteData {
    return (
        'number' in item &&
        !('bundleNumber' in item) &&
        !('tourNumber' in item) &&
        !('eventType' in item)
    );
}

export function isEventData(item: SelectableItem): item is EventWithOrgSiteKey {
    return 'eventType' in item || 'eventKey' in item;
}

export function isBundleGroupData(
    item: SelectableItem
): item is BundleGroupData {
    return (
        'bundleKeys' in item &&
        'deliveryNoteKeys' in item &&
        'consignorKey' in item &&
        'consigneeKey' in item &&
        !('tourNumber' in item) &&
        !('bundleNumber' in item)
    );
}

export function filterFolderData(items: SelectableItem[]): FolderData[] {
    return items.filter(isFolderData);
}

export function filterTourData(items: SelectableItem[]): TourData[] {
    return items.filter(isTourData);
}

export function filterDeliveryNoteData(
    items: SelectableItem[]
): DeliveryNoteData[] {
    return items.filter(isDeliveryNoteData);
}

export function filterEventData(
    items: SelectableItem[]
): EventWithOrgSiteKey[] {
    return items.filter(isEventData);
}

export function filterBundleGroupData(
    items: SelectableItem[]
): BundleGroupData[] {
    return items.filter(isBundleGroupData);
}

// =============================================================================
// NgRx SignalStore
// =============================================================================

export const SelectionStore = signalStore(
    { providedIn: 'root' },
    withState<SelectionState>(initialSelectionState),
    withComputed(({ selectedItems }) => ({
        selectionCount: computed(() => selectedItems().length),
        hasSelection: computed(() => selectedItems().length > 0),
        hasSingleSelection: computed(() => selectedItems().length === 1),
        hasMultipleSelection: computed(() => selectedItems().length > 1),
        isSelectionValidForCheckin: computed(() => {
            const items = selectedItems();
            return (
                items.length > 0 &&
                items.every(item => {
                    const status = hasStatus(item) ? item.status : null;
                    const readOnly = hasReadOnly(item) ? item.read_only : false;
                    return status !== 'closed' && !readOnly;
                })
            );
        }),
        checkinReadyCount: computed(() => {
            const items = selectedItems();
            return items.filter(item => {
                const status = hasStatus(item) ? item.status : null;
                const readOnly = hasReadOnly(item) ? item.read_only : false;
                return status !== 'closed' && !readOnly;
            }).length;
        }),
    })),
    withMethods(store => ({
        setSelection(
            items: SelectableItem[],
            contextData?: SelectionContextData
        ): void {
            patchState(store, state => ({
                selectedItems: items,
                lastSelectedItem:
                    items.length > 0 ? items[items.length - 1] : null,
                contextData: contextData || state.contextData,
            }));
        },
        addToSelection(item: SelectableItem): void {
            const itemKey = getItemKey(item);
            const existingItem = store.selectedItems().find(
                selectedItem => getItemKey(selectedItem) === itemKey
            );
            if (existingItem) {
                return;
            }
            patchState(store, state => {
                const newItems = [...state.selectedItems, item];
                return {
                    selectedItems: newItems,
                    lastSelectedItem: item,
                };
            });
        },
        removeFromSelection(item: SelectableItem): void {
            const itemKey = getItemKey(item);
            patchState(store, state => {
                const newItems = state.selectedItems.filter(
                    selected => getItemKey(selected) !== itemKey
                );
                return {
                    selectedItems: newItems,
                    lastSelectedItem:
                        newItems.length > 0
                            ? newItems[newItems.length - 1]
                            : null,
                };
            });
        },
        toggleSelection(item: SelectableItem): void {
            const itemKey = getItemKey(item);
            const isSelected = store.selectedItems().some(
                selectedItem => getItemKey(selectedItem) === itemKey
            );
            if (isSelected) {
                this.removeFromSelection(item);
            } else {
                this.addToSelection(item);
            }
        },
        clearSelection(): void {
            patchState(store, {
                selectedItems: [],
                lastSelectedItem: null,
            });
        },
        setSelectionType(type: 'single' | 'multiple'): void {
            patchState(store, state => {
                const newSelectedItems =
                    type === 'single' && state.selectedItems.length > 1
                        ? [state.lastSelectedItem!].filter(Boolean)
                        : state.selectedItems;
                return {
                    selectionType: type,
                    selectedItems: newSelectedItems,
                };
            });
        },
        selectAll(items: SelectableItem[]): void {
            patchState(store, {
                selectedItems: [...items],
                lastSelectedItem:
                    items.length > 0 ? items[items.length - 1] : null,
            });
        },
        isItemSelected(item: SelectableItem): boolean {
            const itemKey = getItemKey(item);
            return store.selectedItems().some(
                selectedItem => getItemKey(selectedItem) === itemKey
            );
        },
        setContextData(contextData: SelectionContextData): void {
            patchState(store, { contextData });
        },
    }))
);
